import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { ConfigViewer } from '@/components/ConfigViewer';
import { useNginxConfigDetail } from '@/hooks/data/useNginxConfigs';
import { formatDateTime } from '@/utils/date';
import { ActiveBadge, ReloadStatusIndicator } from './NginxStatus';

export interface ViewConfigModalProps {
  /** Config to show; the modal is open while it is set */
  configId: string | null;
  onClose: () => void;
}

/**
 * Shows the full content and metadata of an nginx config
 */
export function ViewConfigModal({ configId, onClose }: ViewConfigModalProps) {
  const { config, isLoading, error } = useNginxConfigDetail(configId);

  return (
    <Modal isOpen={configId !== null} onClose={onClose} size="lg">
      <ModalHeader>View NGINX Configuration</ModalHeader>
      <ModalBody className="nginx-view-modal-body">
        {isLoading ? (
          <div className="nginx-loading">
            <div className="nginx-spinner" />
            <span>Loading configuration...</span>
          </div>
        ) : error ? (
          <div className="modal-error" role="alert">
            {error}
          </div>
        ) : config ? (
          <>
            {/* Config Metadata */}
            <div className="nginx-view-meta">
              <div className="nginx-meta-item">
                <span className="nginx-meta-label">Node</span>
                <span className="nginx-meta-value">
                  {config.node ? (
                    <>
                      <span className="nginx-meta-name">{config.node.name}</span>
                      <span className="nginx-meta-ip">({config.node.ip_address})</span>
                    </>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="nginx-meta-item">
                <span className="nginx-meta-label">Domain</span>
                <span className="nginx-meta-value">{config.domain?.name || '-'}</span>
              </div>
              <div className="nginx-meta-item">
                <span className="nginx-meta-label">Status</span>
                <span className="nginx-meta-value">
                  <ActiveBadge isActive={config.is_active} />
                </span>
              </div>
              <div className="nginx-meta-item">
                <span className="nginx-meta-label">Last Reload</span>
                <span className="nginx-meta-value">
                  <span>{formatDateTime(config.last_reload_at)}</span>
                  {config.last_reload_status && <ReloadStatusIndicator status={config.last_reload_status} />}
                </span>
              </div>
            </div>

            {/* Error Message */}
            {config.last_reload_error && (
              <div className="nginx-view-error">
                <strong>Last Reload Error:</strong>
                <span>{config.last_reload_error}</span>
              </div>
            )}

            {/* Config Content */}
            <ConfigViewer
              content={config.config_content}
              title={`${config.domain?.name || 'config'}.conf`}
              maxHeight="400px"
            />
          </>
        ) : (
          <div className="nginx-view-empty">No configuration loaded.</div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
